import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import PropTypes from "prop-types";
import clientphotoApiController from "../../../../services/clientphoto.service";

const AuthenticatedClientPhoto = ({ photo, alt = "Client photo", link = false, allowDocument = false, className = "" }) => {
  const [state, setState] = useState({ status: "loading", source: "" });
  const photoId = photo && photo.id;
  const photoRevision = photo && (photo.revision || photo.updated_at || photo.byte_size);
  const authToken = useSelector((storeState) => storeState.auth && storeState.auth.token);

  useEffect(() => {
    let active = true;
    let objectUrl = "";
    const controller = new AbortController();
    setState({ status: "loading", source: "" });

    clientphotoApiController
      .content({ id: photoId, signal: controller.signal })
      .then((response) => {
        const blob = response && response.data;
        if (!(blob instanceof Blob) || !(["image/png", "image/jpeg", "image/webp"].includes(blob.type) || (allowDocument && !String(photoId).startsWith("avatar-") && blob.type === "application/pdf"))) {
          throw new Error("Invalid client photo response.");
        }
        objectUrl = window.URL.createObjectURL(blob);
        if (active) {
          setState({ status: "ready", source: objectUrl, type: blob.type });
        } else if (window.URL && window.URL.revokeObjectURL) {
          window.URL.revokeObjectURL(objectUrl);
          objectUrl = "";
        }
      })
      .catch(() => {
        if (active) setState({ status: "error", source: "" });
      });

    return () => {
      active = false;
      controller.abort();
      if (objectUrl && window.URL && window.URL.revokeObjectURL) {
        window.URL.revokeObjectURL(objectUrl);
      }
    };
  }, [authToken, photoId, photoRevision, allowDocument]);

  if (state.status === "error") {
    return <span role="status">Photo unavailable</span>;
  }
  if (state.status !== "ready") {
    return <span role="status">Loading photo</span>;
  }

  if (state.type === "application/pdf") {
    const filename = (photo.original_filename || photo.photo || "client-document.pdf").split(/[\\/]/).pop();
    return <a href={state.source} download={filename}>Download PDF</a>;
  }

  const image = <img src={state.source} alt={alt} className={className} />;
  return link ? (
    <a data-fancybox="" data-src={state.source}>
      {image}
    </a>
  ) : image;
};

AuthenticatedClientPhoto.propTypes = {
  photo: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
    updated_at: PropTypes.string,
    revision: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    byte_size: PropTypes.number,
    original_filename: PropTypes.string,
    photo: PropTypes.string,
  }).isRequired,
  alt: PropTypes.string,
  link: PropTypes.bool,
  allowDocument: PropTypes.bool,
  className: PropTypes.string,
};

export default AuthenticatedClientPhoto;
